import { useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { X } from 'lucide-react';
import {
    getFunnelDetail,
    getFunnelNodes,
    FunnelRevenueBatch,
    NodeStat,
    NodeTransition,
} from '@/services/api/meta-analytics';
import { fmtMoney, fmtN, fmtPct, TZ_LABEL } from './utils';

function Skeleton({ className = '' }: { className?: string }) {
    return <div className={`animate-pulse bg-gray-100 rounded ${className}`} />;
}

const NODE_ICONS: Record<string, string> = {
    trigger: '⚡',
    photo_template: '🖼',
    message_template: '💬',
    delay: '⏱',
    go_to: '➡',
};

function fmtDelay(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
    return `${Math.round(seconds / 3600)}h`;
}

function NodeFlowMap({ nodes }: { nodes: NodeStat[]; transitions: NodeTransition[] }) {
    if (!nodes.length) return <p className="text-sm text-gray-400">Sem nodes.</p>;

    return (
        <div className="flex flex-col items-stretch gap-0">
            {nodes.map((node, idx) => {
                const next = nodes[idx + 1];
                const dropOff = next && node.unique_leads > 0 && next.unique_leads > 0
                    ? node.unique_leads - next.unique_leads
                    : null;
                const dropOffPct = dropOff !== null && node.unique_leads > 0
                    ? (dropOff / node.unique_leads) * 100
                    : null;

                return (
                    <div key={node.node_id} className="flex flex-col items-stretch">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-base leading-none">
                                    {NODE_ICONS[node.tipo] ?? '●'}
                                </span>
                                <span className="text-sm font-medium text-gray-800 flex-1 min-w-0 truncate">
                                    {node.nome}
                                </span>
                                <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 shrink-0">
                                    {node.tipo}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {node.unique_leads > 0 && (
                                    <span className="text-xs font-medium text-blue-600">
                                        {fmtN(node.unique_leads)} leads únicos
                                    </span>
                                )}
                                {node.clicks > 0 && (
                                    <span className="text-xs font-medium text-emerald-600">
                                        {fmtN(node.clicks)} cliques
                                    </span>
                                )}
                                {node.leads_waiting > 0 && (
                                    <span className="text-xs font-medium text-amber-500">
                                        {fmtN(node.leads_waiting)} aguardando
                                    </span>
                                )}
                                {node.delay_seconds != null && node.delay_seconds > 0 && (
                                    <span className="text-xs font-medium text-gray-500">
                                        ⏱ {fmtDelay(node.delay_seconds)}
                                    </span>
                                )}
                                {node.goto_funnel_id != null && (
                                    <span className="text-xs font-medium text-violet-600">
                                        → Flow {node.goto_funnel_id}
                                    </span>
                                )}
                            </div>
                        </div>

                        {idx < nodes.length - 1 && (
                            <div className="flex flex-col items-center py-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-200 text-sm leading-none">↓</span>
                                    {dropOff !== null && dropOffPct !== null && dropOff > 0 && (
                                        <span className="text-xs font-medium text-red-400">
                                            -{fmtN(dropOff)} ({fmtPct(dropOffPct, 0)})
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

interface Props {
    funnelId: number;
    funnelTitle: string;
    start: string;
    end: string;
    revenueData?: FunnelRevenueBatch;
    onClose: () => void;
}

export function FunnelPanel({ funnelId, funnelTitle, start, end, revenueData, onClose }: Props) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const detailQuery = useQuery({
        queryKey: ['funnel-detail-v2', funnelId, start, end],
        queryFn: () => getFunnelDetail(funnelId, start, end),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    const nodesQuery = useQuery({
        queryKey: ['funnel-nodes', funnelId, start, end],
        queryFn: () => getFunnelNodes(funnelId, start, end),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    const detail = detailQuery.data?.data;

    const topCountries = useMemo(() => {
        if (!detail?.country_clicks?.length) return [];
        return [...detail.country_clicks].sort((a, b) => b.clicks - a.clicks).slice(0, 10);
    }, [detail]);

    const totalClicks  = detail?.hourly_clicks.reduce((s, d) => s + d.clicks, 0) ?? 0;
    const totalLeads   = detail?.hourly_clicks.reduce((s, d) => s + d.unique_leads, 0) ?? 0;
    const totalRevenue = revenueData?.total_revenue ?? 0;
    const rpc          = totalClicks > 0 ? fmtMoney(totalRevenue / totalClicks * 1000) : '—';

    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/40"
            onClick={e => { if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose(); }}
        >
            <div ref={panelRef} className="relative h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto flex flex-col">

                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <h2 className="font-semibold text-gray-900 text-base truncate" title={funnelTitle}>{funnelTitle}</h2>
                        <p className="text-xs text-gray-400 mt-0.5">flow_{funnelId} · {start} → {end}</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors shrink-0">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 px-6 py-6 space-y-8">

                    {/* Mini funil */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Funil</h3>
                        <div className="flex items-center gap-2 flex-wrap rounded-xl bg-gray-50 p-4">
                            <div className="flex-1 min-w-[90px] text-center">
                                {detailQuery.isLoading ? <Skeleton className="h-7 w-16 mx-auto mb-1" /> : (
                                    <p className="text-xl font-bold text-indigo-600">{fmtN(totalClicks)}</p>
                                )}
                                <p className="text-xs text-gray-400">Cliques</p>
                            </div>
                            <div className="text-gray-300 font-bold text-lg shrink-0">→</div>
                            <div className="flex-1 min-w-[90px] text-center">
                                {detailQuery.isLoading ? <Skeleton className="h-7 w-16 mx-auto mb-1" /> : (
                                    <>
                                        <p className="text-xl font-bold text-emerald-600">{fmtN(totalLeads)}</p>
                                        <p className="text-[10px] text-emerald-400">
                                            {totalClicks > 0 ? fmtPct((totalLeads / totalClicks) * 100, 1) + ' únicos' : '—'}
                                        </p>
                                    </>
                                )}
                                <p className="text-xs text-gray-400">Clicadores únicos</p>
                            </div>
                            <div className="text-gray-300 font-bold text-lg shrink-0">→</div>
                            <div className="flex-1 min-w-[90px] text-center">
                                {revenueData == null ? <Skeleton className="h-7 w-20 mx-auto mb-1" /> : (
                                    <p className={`text-xl font-bold ${totalRevenue > 0 ? 'text-emerald-600' : 'text-red-400'}`}>
                                        {totalRevenue > 0 ? fmtMoney(totalRevenue) : 'Sem revenue'}
                                    </p>
                                )}
                                <p className="text-xs text-gray-400">Revenue</p>
                            </div>
                        </div>
                        {totalRevenue > 0 && (
                            <p className="text-xs text-gray-400 mt-2 px-1">
                                RPM cliques: <span className="font-medium text-gray-600">{rpc}</span>
                            </p>
                        )}
                    </section>

                    {/* Revenue por domain */}
                    {revenueData && revenueData.by_domain.length > 0 && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Revenue por domain</h3>
                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Domain</th>
                                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Revenue</th>
                                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Impressões</th>
                                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">eCPM</th>
                                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">CTR</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {revenueData.by_domain.map(d => (
                                            <tr key={d.domain} className="hover:bg-gray-50">
                                                <td className="px-3 py-2.5 font-medium text-gray-800">{d.domain}</td>
                                                <td className="px-3 py-2.5 text-right font-semibold text-emerald-600">{fmtMoney(d.revenue)}</td>
                                                <td className="px-3 py-2.5 text-right text-gray-600">{fmtN(d.impressions)}</td>
                                                <td className="px-3 py-2.5 text-right text-gray-600">{fmtMoney(d.ecpm)}</td>
                                                <td className="px-3 py-2.5 text-right text-gray-600">{fmtPct(d.ctr, 1)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* Cliques por hora */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Cliques por hora</h3>
                        {detailQuery.isLoading ? <Skeleton className="h-36" /> : (detail?.hourly_clicks ?? []).length === 0 ? (
                            <p className="text-sm text-gray-400">Sem dados.</p>
                        ) : (
                            <div className="rounded-xl border border-gray-100 p-4">
                                <ResponsiveContainer width="100%" height={140}>
                                    <BarChart
                                        data={Array.from({ length: 24 }, (_, h) => {
                                            const found = detail!.hourly_clicks.find(d => d.hour === h);
                                            return { label: `${String(h).padStart(2,'0')}h`, clicks: found?.clicks ?? 0 };
                                        })}
                                        margin={{ top: 2, right: 4, left: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={1} />
                                        <YAxis tick={{ fontSize: 10 }} width={32} tickFormatter={v => fmtN(v as number)} />
                                        <Tooltip formatter={(v) => fmtN(Number(v ?? 0))} />
                                        <Bar dataKey="clicks" name="Cliques" radius={[2,2,0,0]}>
                                            {Array.from({ length: 24 }, (_, h) => {
                                                const found = detail!.hourly_clicks.find(d => d.hour === h);
                                                const v = found?.clicks ?? 0;
                                                const max = Math.max(...detail!.hourly_clicks.map(d => d.clicks), 1);
                                                const i = v / max;
                                                return <Cell key={h} fill={i > 0.6 ? '#10b981' : i > 0.3 ? '#34d399' : i > 0 ? '#a7f3d0' : '#e5e7eb'} />;
                                            })}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </section>

                    {/* Regiões */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Regiões — cliques por timezone</h3>
                        {detailQuery.isLoading ? (
                            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
                        ) : topCountries.length === 0 ? (
                            <p className="text-sm text-gray-400">Sem dados de região.</p>
                        ) : (
                            <div className="rounded-xl border border-gray-100 p-4">
                                <ResponsiveContainer width="100%" height={Math.max(topCountries.length * 36, 180)}>
                                    <BarChart
                                        layout="vertical"
                                        data={topCountries.map(row => ({
                                            name: TZ_LABEL[row.country] ?? row.country,
                                            cliques: row.clicks,
                                            leads: row.unique_leads,
                                        }))}
                                        margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => fmtN(v as number)} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={96} />
                                        <Tooltip formatter={(v, name) => [fmtN(Number(v ?? 0)), name === 'cliques' ? 'Cliques' : 'Clicadores únicos']} />
                                        <Legend />
                                        <Bar dataKey="cliques" name="Cliques" fill="#10b981" radius={[0,3,3,0]} />
                                        <Bar dataKey="leads" name="Clicadores únicos" fill="#6366f1" radius={[0,3,3,0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </section>

                    {/* Mapa de Nodes */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Mapa de Nodes</h3>
                        {nodesQuery.isLoading ? (
                            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
                        ) : nodesQuery.data?.data ? (
                            <NodeFlowMap
                                nodes={nodesQuery.data.data.nodes}
                                transitions={nodesQuery.data.data.transitions}
                            />
                        ) : (
                            <p className="text-sm text-gray-400">Sem dados de nodes.</p>
                        )}
                    </section>

                </div>
            </div>
        </div>
    );
}
